import {AppDispatchType} from 'app/store/AppStore';

import {taskDispatcher} from 'app/subscriptions/taskDispatcher';
import {
  showNotification,
  showUpgradeModal,
  UPGRADE_MODALS,
} from 'app/toasts/slice';
import {
  POLICIES_API_URL_COPY,
  POLICY_VERSION_SIGNOFFREQUEST_API_URL,
  PROCEDURE_VERSION_SIGNOFFREQUEST_API_URL,
  PROCEDURES_API_URL_COPY,
  PROCESS_API_URL_COPY,
  PROCESS_VERSION_SIGNOFFREQUEST_API_URL,
  STATUSES,
  USERDATA_SWITCHACCOUNT_API_URL,
} from 'app/urls';
import {gt} from 'app/utils/i18n';
import {postJson} from 'app/utils/request';
import * as request from 'app/utils/request';

import {
  BaseDocumentContentType,
  CloneableContentType,
  DetailPolicySerializedType,
  GenericRelatedSerializedType,
  ProcedureSerializedType,
} from 'sweet-types';

export const switchAccount =
  (accountId: number, targetURL: string) =>
  (dispatch: AppDispatchType): Promise<unknown> => {
    return postJson(USERDATA_SWITCHACCOUNT_API_URL, {account_id: accountId})
      .then(() => (window.location.href = targetURL))
      .catch(
        request.createErrorHandler(
          gt('Could not switch to the other account'),
          dispatch,
          {
            '400': (error, dispatch) => {
              dispatch(
                showNotification(
                  'Could not switch to the other account',
                  'error'
                )
              );
              throw error;
            },
          }
        )
      );
  };

export interface ClonePayloadType {
  url: string;
  name: string;
  contentType: CloneableContentType;
  teams: Array<string>;
  folders: Array<string>;
  duplicateProcedures?: boolean;
}

type CloneResultType =
  | ProcedureSerializedType
  | DetailPolicySerializedType
  | {
      html_url: string;
      name: string;
    };

export const duplicate =
  ({
    url,
    name,
    contentType,
    teams,
    folders,
    duplicateProcedures,
  }: ClonePayloadType) =>
  (
    dispatch: AppDispatchType
  ): Promise<
    | CloneResultType
    | {
        status: 'complete';
        result: CloneResultType;
      }
  > => {
    const cloneurl = {
      procedure: PROCEDURES_API_URL_COPY,
      proceduregroup: PROCESS_API_URL_COPY,
      policy: POLICIES_API_URL_COPY,
    }[contentType];

    if (contentType === 'proceduregroup') {
      return dispatch(
        taskDispatcher(
          request
            .postJson(cloneurl, {
              url,
              name,
              teams,
              folders,
              duplicate_procedures: duplicateProcedures,
            })
            .catch(
              request.createErrorHandler(gt('Unable to duplicate'), dispatch, {
                '403': (error) => {
                  if (error.data.detail === STATUSES.UPGRADE_REQUIRED) {
                    return dispatch(
                      showUpgradeModal(UPGRADE_MODALS.PROCEDURE_LIMIT)
                    );
                  } else {
                    return dispatch(
                      showNotification(error.data.detail, 'error')
                    );
                  }
                },
              })
            )
        )
      );
    } else {
      return request
        .postJson<CloneResultType>(cloneurl, {
          url,
          name,
          teams,
          folders,
          duplicate_procedures: duplicateProcedures,
        })
        .catch(
          request.createErrorHandler(gt('Unable to duplicate'), dispatch, {
            '403': (error) => {
              if (error.data.detail === STATUSES.UPGRADE_REQUIRED) {
                dispatch(showUpgradeModal(UPGRADE_MODALS.PROCEDURE_LIMIT));
              }
              throw error;
            },
          })
        );
    }
  };

export interface SignoffRequestPayloadType {
  current_version: GenericRelatedSerializedType<
    'procedureversion' | 'processversion' | 'policyversion' | 'quizversion'
  >;
  contentType: BaseDocumentContentType;
}

export const signoffRequest =
  ({current_version, contentType}: SignoffRequestPayloadType) =>
  (dispatch: AppDispatchType): Promise<unknown> => {
    const signoffurl = {
      procedure: PROCEDURE_VERSION_SIGNOFFREQUEST_API_URL,
      proceduregroup: PROCESS_VERSION_SIGNOFFREQUEST_API_URL,
      policy: POLICY_VERSION_SIGNOFFREQUEST_API_URL,
    }[contentType];

    return request
      .postJson(signoffurl, {
        url: current_version.url,
      })
      .catch(
        request.createErrorHandler(gt('Unable to Require Sign Off'), dispatch, {
          '403': (error) => {
            if (error.data.detail === STATUSES.UPGRADE_REQUIRED) {
              return dispatch(showUpgradeModal(UPGRADE_MODALS.PROCEDURE_LIMIT));
            }
          },
        })
      );
  };
